import React, { useState, useEffect } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faAngleLeft, faAngleRight, faSpinner } from '@fortawesome/free-solid-svg-icons';
import { useNavigate, useLocation } from 'react-router-dom';
import { toast } from 'react-toastify';
import PageHeadingSection from '../../../../common/pageheading/PageHeadingSection';
import apiServices from '../../../../../services/apiService';
import CustomSelect from '../../../../common/formfields/CustomSelect';
import { primaryColor, secondaryColor } from '../../../../../utils/comon';
import Breadcrumbs from '../../../../common/BreadCrum/BreadCrum';

const MenuForm = () => {
    const navigate = useNavigate();
    const location = useLocation();
    const [isLoading, setIsLoading] = useState(false);
    const [isFetching, setIsFetching] = useState(false);

    const [parentMenus, setParentMenus] = useState([]);

    const [formData, setFormData] = useState({
        menuName: '',
        type: 'Parent',
        parentId: null,
        url: '',
        menuIcon: '',
        isActive: 'Active'
    });

    const isEdit = !!location.state?.id;
    const editId = location.state?.id;
    const initialData = location.state?.rowData;

    useEffect(() => {
        const fetchMasterData = async () => {
            try {
                // Fetch Parent Menus for the dropdown
                const menuRes = await apiServices({ method: 'GET', url: '/Residential/ResMenuList/getAllMenuList' });
                if (menuRes.success || menuRes.isSuccess || menuRes.IsSuccess) {
                    const menuData = menuRes.data?.menuListResponse || menuRes.menuListResponse || menuRes.data || [];
                    const parents = (Array.isArray(menuData) ? menuData : []).filter(m => m.type === 'Parent');
                    setParentMenus(parents.map(m => ({
                        label: m.menuName,
                        value: m.menuListId || m.id
                    })));
                }

                // Always fetch the menu list to find the exact item for edit (matching Angular's logic)
                if (isEdit) {
                    setIsFetching(true);
                    try {
                        const res = await apiServices({ method: 'GET', url: `/Residential/ResMenuList/getAllMenuList` });
                        if (res.success || res.isSuccess || res.IsSuccess) {
                            const data = res.data?.menuListResponse || res.menuListResponse || res.data || [];
                            const item = (Array.isArray(data) ? data : []).find(m => String(m.menuListId) === String(editId) || String(m.id) === String(editId));
                            if (item) {
                                setFormData({
                                    menuName: item.menuName || '',
                                    type: item.type || 'Parent',
                                    parentId: item.parentId || null,
                                    url: item.menuUrl || item.url || '',
                                    menuIcon: item.menuIcon || '',
                                    isActive: item.isActive || 'Active'
                                });
                            }
                        }
                    } catch (err) {
                        console.error(err);
                    }
                }
            } catch (error) {
                console.error("Error fetching data:", error);
                toast.error("Failed to load necessary data.");
            } finally {
                setIsFetching(false);
            }
        };

        fetchMasterData();
    }, [isEdit, editId, initialData]);

    const handleInputChange = (e) => {
        const { id, value } = e.target;
        setFormData(prev => ({ ...prev, [id]: value }));
    };

    const handleSelectChange = (key, option) => {
        setFormData(prev => {
            const newData = { ...prev, [key]: option ? option.value : null };
            if (key === 'type' && option?.value === 'Parent') {
                newData.parentId = null; // Clear parentId if type changes to Parent
            }
            return newData;
        });
    };

    const validateForm = () => {
        if (!formData.menuName || !formData.menuName.trim()) return toast.warning("Menu Name is required"), false;
        if (!formData.type) return toast.warning("Menu Type is required"), false;
        if (formData.type === 'Child' && !formData.parentId) return toast.warning("Parent Menu is required for Child type"), false;
        if (!formData.isActive) return toast.warning("Status is required"), false;
        return true;
    };

    const handleSubmit = async () => {
        if (!validateForm()) return;
        setIsLoading(true);

        const payload = {
            menuName: formData.menuName,
            type: formData.type,
            parentId: formData.parentId || null, // Allow parentId for Parent type as per Angular
            menuUrl: formData.url,
            menuIcon: formData.menuIcon,
            createdBy: 'sys', // default fallback like Angular's 'Ritu'
            isActiveId: formData.isActive === 'Active' ? 1 : 0
        };

        try {
            const url = isEdit ? `/Residential/ResMenuList/putMenuList/${editId}` : `/Residential/ResMenuList/postMenuList`;
            const method = isEdit ? 'PUT' : 'POST';

            const response = await apiServices({ method, url, data: payload });

            if (response.success || response.isSuccess || response.IsSuccess) {
                toast.success(`Menu ${isEdit ? 'updated' : 'created'} successfully!`);
                navigate('/zentres/site-management/menu');
            } else {
                toast.error(response.message || `Failed to ${isEdit ? 'update' : 'create'} menu.`);
            }
        } catch (error) {
            console.error("Submission error:", error);
            toast.error("An error occurred during submission.");
        } finally {
            setIsLoading(false);
        }
    };

    if (isFetching) {
        return (
            <div className="flex-1 flex justify-center items-center h-full">
                <FontAwesomeIcon icon={faSpinner} className="animate-spin text-4xl text-gray-300" />
            </div>
        );
    }

    return (
        <div className="bg-dashboardbg px-6 py-4 border border-bordercolor flex flex-col rounded-lg flex-1 overflow-y-auto pt-4">
            <Breadcrumbs />
            <PageHeadingSection
                Navbar={true}
                mainHeading={`${isEdit ? 'Update' : 'Add'} Menu Item`}
                subHeading={`Please fill out the form below to ${isEdit ? 'update the existing' : 'create a new'} menu item.`}
            />

            <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mt-4">
                <div>
                    <label className="block text-xs font-bold text-gray-700 dark:text-textsecondary mb-1" htmlFor="menuName">
                        Menu Name <span className="text-red-500">*</span>
                    </label>
                    <input
                        className="w-full px-3 py-2 border border-gray-200 dark:border-bordercolor rounded-md text-sm outline-none bg-transparent text-textprimary placeholder:text-gray-400 focus:border-gray-400 dark:focus:border-gray-500 transition-colors"
                        type="text"
                        id="menuName"
                        value={formData.menuName}
                        onChange={handleInputChange}
                        placeholder="e.g., Dashboard"
                    />
                </div>

                <CustomSelect
                    label={<>Menu Type <span className="text-red-500">*</span></>}
                    options={[{ label: 'Parent', value: 'Parent' }, { label: 'Child', value: 'Child' }]}
                    value={formData.type ? { label: formData.type, value: formData.type } : null}
                    onChange={(opt) => handleSelectChange('type', opt)}
                />

                {formData.type === 'Child' ? (
                    <CustomSelect
                        label={<>Parent Menu <span className="text-red-500">*</span></>}
                        options={parentMenus}
                        value={parentMenus.find(p => p.value === formData.parentId) || null}
                        onChange={(opt) => handleSelectChange('parentId', opt)}
                    />
                ) : (
                    <div>
                        <label className="block text-xs font-bold text-gray-700 dark:text-textsecondary mb-1" htmlFor="parentId">
                            Parent ID
                        </label>
                        <input
                            className="w-full px-3 py-2 border border-gray-200 dark:border-bordercolor rounded-md text-sm outline-none bg-transparent text-textprimary placeholder:text-gray-400 focus:border-gray-400 dark:focus:border-gray-500 transition-colors"
                            type="text"
                            id="parentId"
                            value={formData.parentId || ''}
                            onChange={handleInputChange}
                            placeholder="Enter Parent ID"
                        />
                    </div>
                )}

                <div>
                    <label className="block text-xs font-bold text-gray-700 dark:text-textsecondary mb-1" htmlFor="url">
                        URL Path
                    </label>
                    <input
                        className="w-full px-3 py-2 border border-gray-200 dark:border-bordercolor rounded-md text-sm outline-none bg-transparent text-textprimary placeholder:text-gray-400 focus:border-gray-400 dark:focus:border-gray-500 transition-colors"
                        type="text"
                        id="url"
                        value={formData.url}
                        onChange={handleInputChange}
                        placeholder="e.g., /dashboard"
                    />
                </div>

                <div>
                    <label className="block text-xs font-bold text-gray-700 dark:text-textsecondary mb-1" htmlFor="menuIcon">
                        Menu Icon (GoogleFonts Class)
                    </label>
                    <input
                        className="w-full px-3 py-2 border border-gray-200 dark:border-bordercolor rounded-md text-sm outline-none bg-transparent text-textprimary placeholder:text-gray-400 focus:border-gray-400 dark:focus:border-gray-500 transition-colors"
                        type="text"
                        id="menuIcon"
                        value={formData.menuIcon}
                        onChange={handleInputChange}
                        placeholder="e.g., fa-solid fa-home"
                    />
                </div>

                <CustomSelect
                    label={<>Status <span className="text-red-500">*</span></>}
                    options={[{ label: 'Active', value: 'Active' }, { label: 'Inactive', value: 'Inactive' }]}
                    value={formData.isActive ? { label: formData.isActive, value: formData.isActive } : null}
                    onChange={(opt) => handleSelectChange('isActive', opt)}
                />
            </div>

            <div className="flex justify-between mt-auto border-t pt-4">
                <button
                    onClick={() => navigate("/zentres/site-management/menu")}
                    className="px-3 py-2 border dark:border-bordercolor rounded-md text-xs flex items-center gap-2 text-gray-600 dark:text-textprimary hover:bg-gray-50 dark:hover:bg-actionitembg transition-colors"
                >
                    <FontAwesomeIcon icon={faAngleLeft} />
                    Cancel
                </button>

                <button
                    onClick={handleSubmit}
                    disabled={isLoading}
                    className="px-4 py-2 rounded-md text-xs font-bold flex items-center gap-2 shadow-sm hover:shadow-md active:scale-95 transition-all duration-200"
                    style={{ backgroundColor: secondaryColor(), color: primaryColor() }}
                    onMouseEnter={(e) => {
                        e.currentTarget.style.backgroundColor = primaryColor();
                        e.currentTarget.style.color = '#FFFFFF';
                    }}
                    onMouseLeave={(e) => {
                        e.currentTarget.style.backgroundColor = secondaryColor();
                        e.currentTarget.style.color = primaryColor();
                    }}
                >
                    {isLoading ? <FontAwesomeIcon icon={faSpinner} className="animate-spin" /> : (isEdit ? 'Update' : 'Submit')}
                    <FontAwesomeIcon icon={faAngleRight} />
                </button>
            </div>
        </div>
    );
};

export default MenuForm;
